import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileDown, Trash2, ClipboardCheck, Star, ChevronDown } from 'lucide-react';
import { RatingBadge } from './RatingBadge';
import { RatingTrend } from './RatingTrend';
import { API, fmtDate, ratingMeta, CATEGORY_LABELS } from './groupMeta';

export const downloadInspectionPdf = async (inspection, getAuthHeaders) => {
  try {
    const r = await axios.get(`${API}/inspections/${inspection.id}/pdf`, { headers: getAuthHeaders(), responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = url; a.download = `inspection-${inspection.date.slice(0, 10)}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error('PDF download failed'); }
};

const Block = ({ label, text }) => text ? <div><p className="text-[9px] tracking-[0.18em] uppercase text-white/40 font-semibold mb-1">{label}</p><p className="text-sm text-white/75 whitespace-pre-line">{text}</p></div> : null;

const InspectionItem = ({ i, canDelete, onDelete, getAuthHeaders, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const m = ratingMeta(i.rating);
  return (
    <div className="relative pl-8" data-testid={`inspection-${i.id}`}>
      <span className="absolute left-0 top-4 w-4 h-4 rounded-full border-2" style={{ borderColor: m.hex, background: `${m.hex}33`, boxShadow: `0 0 12px ${m.hex}` }} />
      <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] overflow-hidden">
        <button onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left" data-testid={`inspection-toggle-${i.id}`}>
          <RatingBadge rating={i.rating} />
          <span className="text-sm text-white font-medium">{fmtDate(i.date)}</span>
          <span className="text-[11px] text-white/40">by {i.inspector_name}</span>
          <span className="flex-1 text-xs text-white/45 truncate">{i.summary}</span>
          <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="px-4 pb-4 space-y-3 border-t border-white/[0.05] pt-3">
            {Object.keys(i.scores || {}).length > 0 && (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(i.scores).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between px-3 py-2 rounded-xl bg-black/30 border border-white/[0.05]">
                    <span className="text-[11px] text-white/60">{CATEGORY_LABELS[k] || k}</span>
                    <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map(n => <Star key={n} className={`w-3 h-3 ${n <= v ? 'fill-amber-300 text-amber-300' : 'text-white/15'}`} />)}</span>
                  </div>
                ))}
              </div>
            )}
            <Block label="Summary" text={i.summary} /><Block label="Strengths" text={i.strengths} />
            <Block label="Areas for improvement" text={i.improvements} /><Block label="Inspector notes" text={i.notes} />
            <div className="flex gap-2 pt-1">
              <button onClick={() => downloadInspectionPdf(i, getAuthHeaders)} data-testid={`inspection-pdf-${i.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] border border-cyan-400/30 text-cyan-200 bg-cyan-500/10 hover:bg-cyan-500/20"><FileDown className="w-3.5 h-3.5" /> PDF report</button>
              {canDelete && <button onClick={() => onDelete(i)} data-testid={`inspection-delete-${i.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] border border-rose-400/25 text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const GroupInspectionsTab = ({ group, getAuthHeaders, onChanged, onNew }) => {
  const list = group.inspections || [];
  const remove = async (i) => {
    if (!window.confirm('Delete this inspection record permanently?')) return;
    try { await axios.delete(`${API}/inspections/${i.id}`, { headers: getAuthHeaders() }); toast.success('Inspection deleted'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  return (
    <div className="space-y-5" data-testid="group-inspections-tab">
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4">
        <p className="text-[9px] tracking-[0.2em] uppercase text-white/45 font-semibold mb-2">Rating history</p>
        <RatingTrend inspections={list} />
      </div>
      {group.can_inspect && group.status !== 'archived' && (
        <button onClick={onNew} data-testid="new-inspection-btn-tab" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-[#0a0a14] bg-gradient-to-r from-cyan-400 to-violet-400"><ClipboardCheck className="w-3.5 h-3.5" /> New inspection</button>
      )}
      {list.length === 0 ? <p className="text-sm text-white/40 py-10 text-center">No inspections recorded yet.</p> : (
        <div className="relative space-y-3 before:absolute before:left-[7px] before:top-4 before:bottom-4 before:w-px before:bg-white/[0.08]">
          {list.map((i, idx) => <InspectionItem key={i.id} i={i} defaultOpen={idx === 0} canDelete={group.can_edit} onDelete={remove} getAuthHeaders={getAuthHeaders} />)}
        </div>
      )}
    </div>
  );
};
